import React from "react";
import { Box, Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import StockCard from "components/ui/StockCard";
import { useGlobalContext } from "context/GlobalContext";
import apiClient from "lib/api-client";
import config from "configs/config";
import ColorStone from "assets/icons/ColorStone_Icon.png";
import ColorBox from "assets/icons/ColorBox_Icon.png";
import ColorMetal from "assets/icons/ColorMetal_Icon.png";
import { ReactComponent as BlueDiamond } from "assets/filter/filter header/blue_diamond.svg";

const formatIndianPrice = (price) => {
  if (typeof price !== "number") {
    price = Number(price);
  }

  return new Intl.NumberFormat("en-IN").format(price);
};

const resolveCurrencySymbol = (symbol) => {
  const raw = String(symbol || "").trim();
  if (!raw) return "₹";

  const normalized = raw.replace(/\s+/g, "").toUpperCase();
  if (normalized.includes("$") || normalized.includes("USD")) return "$";
  if (normalized.includes("₹") || normalized.includes("INR")) return "₹";

  return raw;
};

const SubStockSetProductItem = () => {
  const { jewelid } = useParams();
  const { setSearchPlaceHolder } = useGlobalContext();

  const [jsonData, setJsonData] = React.useState(null);
  const [currentLabourChart, setCurrentLabourChart] = React.useState("");

  const fetchUserRateMaster = async () => {
    try {
      const response = await apiClient.get("/api/v1/stocks/getUserRateMaster?type=labour");
      console.log("getUserRateMaster response:", response?.data);
      const resolvedCurrentLabourChart =
        response?.data?.currentLabourChart ||
        "";
      setCurrentLabourChart(resolvedCurrentLabourChart);
      return response?.data;
    } catch (error) {
      console.error("Error fetching user rate master:", error);
      setCurrentLabourChart("");
      return null;
    }
  };

  const fetchData = async () => {
    try {
      const response = await apiClient.get(`${config.baseUrl}/api/v1/stocks/product_details/${jewelid}`);

      if (response?.data?.status === 200) {
        setJsonData(response?.data);
      } else {
        console.error("Error fetching data");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };
  React.useEffect(() => {
    fetchUserRateMaster();
    fetchData();
    setSearchPlaceHolder("Search By Jewel Code or Design Number");
    return () => {
      setSearchPlaceHolder("Search");
    };
  }, []);

  if (!jsonData) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#000",
          pt: 40,
        }}
      >
        <CircularProgress sx={{ color: "#ffffff" }} />
      </Box>
    );
  }
  const renderIconForType = (type) => {
    switch (type) {
      case "Color Stone":
        return <img src={ColorStone} alt="Color Stone Icon" width={24} height={24} />;
      case "Other Material":
        return <img src={ColorBox} alt="Color Box Icon" width={24} height={24} />;
      case "Metal":
        return <img src={ColorMetal} alt="Color Metal Icon" width={24} height={24} />;
      case "Diamond":
        return <BlueDiamond />;
      default:
        return null;
    }
  };

  const renameMap = {
    "Stone(Color Stone)": "Color Stone",
    subTotal: "Sub Total",
  };

  const getUnitForType = (type) => {
    switch (type) {
      case "Metal":
      case "Other Material":
        return "g";
      case "Diamond":
      case "Color Stone":
        return "Ct";
      default:
        return "";
    }
  };

  const { Summary = {}, StockCardDetails = {}, Details = [], TotalPrice } = jsonData || {};
  const summaryCurrencySymbol = resolveCurrencySymbol(Summary?.currencySymbol);
  const order = ["Metal", "Diamond", "Color Stone", "Other Material"];
  const normalizedLabourChart = String(currentLabourChart || "").trim().toUpperCase();
  const useWastageChargesLabel = normalizedLabourChart === "" || normalizedLabourChart.includes("W");
  const chargeLabel = useWastageChargesLabel ? "Wastage Charges" : "Making Charges";
  const preferredChargeSourceKey = useWastageChargesLabel ? "Wastage Charges" : "Making Charges";
  const fallbackChargeSourceKey = useWastageChargesLabel ? "Making Charges" : "Wastage Charges";
  const chargeSourceKey = Object.prototype.hasOwnProperty.call(Summary || {}, preferredChargeSourceKey)
    ? preferredChargeSourceKey
    : fallbackChargeSourceKey;

  const summaryMaterialRows = Object.entries(Summary)
    .filter(([name]) => {
      const isChargeRow = name === "Making Charges" || name === "Wastage Charges";
      const isSupportedRow =
        order.includes(renameMap[name] || name) || isChargeRow || name === "subTotal";
      if (!isSupportedRow) return false;
      return !isChargeRow || name === chargeSourceKey;
    })
    .map(([name, data]) => {
      const renamedName =
        name === "Making Charges" || name === "Wastage Charges"
          ? chargeLabel
          : renameMap[name] || name;
      const unit = renamedName === "Color Stone" || renamedName === "Diamond" ? "Ct" : "g";

      if (name === "subTotal") {
        return {
          sourceName: name,
          name: renamedName,
          rowType: "subtotal",
          price: Number(data) || 0,
        };
      }

      return {
        sourceName: name,
        name: renamedName,
        rowType: "material",
        ...(data || {}),
        unit,
      };
    })
    .sort((a, b) => (order.indexOf(a.name) === -1 ? 999 : order.indexOf(a.name)) - (order.indexOf(b.name) === -1 ? 999 : order.indexOf(b.name)));

  const summaryLevelRows = (Summary?.levelsData || []).map((level) => ({
    sourceName: level?.levelName,
    name: `${level?.levelName || "Charge"} @${level?.levelValue ?? 0}%`,
    rowType: "level",
    price: Number(level?.levelPrice) || 0,
  }));

  const summaryData = [...summaryMaterialRows, ...summaryLevelRows];

  const modifiedDetails = Details.reduce((acc, item) => {
    const rawType = item.ItemType;
    const type = renameMap[rawType] || rawType;

    if (!acc[type]) {
      acc[type] = [];
    }

    acc[type].push(item);
    return acc;
  }, {});

  // const orderedModifiedDetails = order.filter((type) => modifiedDetails[type]).map((type) => [type, modifiedDetails[type]]);
  const orderedModifiedDetails = order.filter((type) => modifiedDetails[type]).map((type) => [type, modifiedDetails[type], getUnitForType(type)]);

  const data = {
    jewelCode: StockCardDetails.jewelCode,
    styleNumber: StockCardDetails.styleNumber,
    diaWt: StockCardDetails.diaWt,
    diaPcs: StockCardDetails.diaPcs,
    goldQlty: StockCardDetails.goldQlty,
    netWt: StockCardDetails.netWt,
    grossWt: StockCardDetails.grossWt,
    csWt: StockCardDetails.csWt,
    csPcs: StockCardDetails.csPcs,
    itemSize: StockCardDetails.itemSize,
    location: StockCardDetails.location,
    setDetails: StockCardDetails.setDetails,
    transformedImageUrl: StockCardDetails?.style?.transformedImageUrl,
    imageUrl: StockCardDetails?.style?.imageUrl,
    designClass: StockCardDetails?.style?.designClass,
    otherWt: StockCardDetails.otherWt,
    otherPcs: StockCardDetails.otherPcs,
    diaQlty: StockCardDetails.diaQlty,
    memoClientName: StockCardDetails.memoClientName,
    reorderData: StockCardDetails?.reorderData || null,
  };
  return (
    <Box
      sx={{
        display: "flex",
        gap: 5,
        flexDirection: "column",
        pb: 6,
        height: "76vh",
        overflow: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      <Box sx={{ display: "flex", gap: 5, flexDirection: { md: "column", lg: "row" }, pt: 10 }}>
        <Grid className="stock">
          <StockCard data={data} />
        </Grid>
        <Box
          sx={{
            //  width: { lg: "822px" },
            width: "100%",
            display: "flex",
            alignItems: "start",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <Typography variant="h6" sx={{ color: "#fff", mb: 2, position: "absolute", left: 0, top: "-15%" }}>
            Summary
          </Typography>
          <TableContainer>
            <Table size="small" sx={{ borderCollapse: "collapse", border: "1px solid #fff" }}>
              <TableHead sx={{ borderBottom: "1px solid #fff" }}>
                <TableRow sx={{ backgroundColor: "#333" }}>
                  <TableCell
                    align="center"
                    sx={{
                      color: "white",
                      py: 2,
                      px: 2,
                      fontFamily: "poppins",
                      fontWeight: "400",
                      fontSize: "14px",
                    }}
                  >
                    Items
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: "white",
                      py: 2,
                      px: 2,
                      fontFamily: "poppins",
                      fontWeight: "400",
                      fontSize: "14px",
                    }}
                  >
                    Rates x Weight
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: "white",
                      py: 2,
                      px: 2,
                      borderLeft: 0,
                      fontFamily: "poppins",
                      fontWeight: "400",
                      fontSize: "14px",
                    }}
                  >
                    Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryData?.map((item, cellIdx) => {
                  const isTotalRow = item.rowType === "subtotal" || item.rowType === "level";
                  const isWastageRow = item.name === "Wastage Charges";
                  const isMakingRow = item.name === "Making Charges";
                  return (
                    <TableRow key={`${item.sourceName}-${cellIdx}`} sx={{ backgroundColor: isTotalRow ? "#FFFFFF0D" : "transparent", fontFamily: "karla" }}>
                      <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                        {item.name}
                      </TableCell>
                      {isTotalRow ? (
                        <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2 }}></TableCell>
                      ) : isWastageRow ? (
                        <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                          {`${item?.avgRate ?? 0}%`}
                        </TableCell>
                      ) : isMakingRow ? (
                        <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                          {`${summaryCurrencySymbol} ${formatIndianPrice(item?.avgRate)}  x  ${item?.weight} g`}
                        </TableCell>
                      ) : (
                        <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                          {`${summaryCurrencySymbol} ${formatIndianPrice(item?.avgRate)}  x  ${item?.weight} ${item?.unit}`}
                        </TableCell>
                      )}

                      <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                        {`${summaryCurrencySymbol} ${formatIndianPrice(item.price)}`}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ backgroundColor: "#141414" }}>
                  <TableCell
                    align="center"
                    sx={{
                      border: "1px solid white",
                      py: 1.2,
                      px: 2,
                      borderRight: 0,
                    }}
                  >
                    {/* <strong>Net Amount</strong> */}
                    Net Amount
                  </TableCell>
                  <TableCell align="center" sx={{ borderBottom: "1px solid white" }}></TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      border: "1px solid white",
                      py: 2,
                      px: 2,
                      borderLeft: 0,
                    }}
                  >
                    {/* <strong>{`₹ ${formatIndianPrice(TotalPrice)}`}</strong> */}
                    {`${summaryCurrencySymbol} ${formatIndianPrice(TotalPrice ?? Summary?.totalPrice ?? 0)}`}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        {orderedModifiedDetails.map(([type, items, unit]) => {
          const totalNetWeight = items.reduce((sum, row) => {
            const weight = typeof row.NetWeight === "number" ? Math.round(row.NetWeight * 1000) / 1000 : parseFloat(row.NetWeight) || 0;
            return Math.round((sum + weight) * 1000) / 1000;
          }, 0);
          const totalPieces = items.reduce((sum, row) => sum + (parseInt(row.Pieces) || 0), 0);
          const totalAmount = items.reduce((sum, row) => {
            const amount = typeof row.Amount === "number" ? Math.round(row.Amount * 100) / 100 : parseFloat(row.Amount) || 0;
            return Math.round((sum + amount) * 100) / 100;
          }, 0);

          return (
            <div key={type}>
              <Box sx={{ display: "flex", gap: "10px", alignItems: "center", justifyContent: "start", marginBottom: "10px" }}>
                <Typography variant="h6" sx={{ color: "#fff", mb: 0, fontFamily: "poppins", fontWeight: "400", fontSize: "20px" }}>
                  {type === "Stone(Color Stone)" ? "Color Stone" : type} details
                </Typography>
                {renderIconForType(type === "Stone(Color Stone)" ? "Color Stone" : type)}
              </Box>

              <TableContainer>
                <Table size="small" sx={{ border: "1px solid white", borderCollapse: "collapse" }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#333" }}>
                      {["Rm Code", "Size", "Wts", "Pcs", "Rate", "Amount"].map((text, i, array) => (
                        <TableCell
                          key={i}
                          align="center"
                          sx={{
                            color: "white",
                            border: i === 0 || i === array.length - 1 ? "1px solid white" : "none",
                            px: 2,
                            py: 2,
                            borderRight: i === 0 ? "none" : "",
                            borderLeft: i === array.length - 1 ? "none" : "",
                            fontFamily: "poppins",
                            fontWeight: "400",
                            fontSize: "14px",
                            width: text === "Size" || text === "Wts" ? "300px" : "auto",
                          }}
                        >
                          {text}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                          {row.ItemCode}
                        </TableCell>
                        <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                          {row.SizeCode === null ? "-" : row.SizeCode}
                        </TableCell>
                        <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                          {row.NetWeight} {unit}
                        </TableCell>
                        <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                          {row.Pieces} Pcs
                        </TableCell>

                        <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                          {`${summaryCurrencySymbol} ${formatIndianPrice(row.Rate)} / ${unit}`}
                        </TableCell>
                        <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                          {`${summaryCurrencySymbol} ${formatIndianPrice(row.Amount)}`}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ background: "#FFFFFF14" }}>
                      <TableCell align="center" sx={{ py: 1.2, border: "1px solid white", borderRight: 0, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                        {type === "Stone(Color Stone)" ? "Color Stone" : type} total
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.2, px: 2, border: "1px solid white", borderLeft: 0 }}></TableCell>
                      <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                        {totalNetWeight} {unit}
                      </TableCell>
                      <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                        {totalPieces} Pcs
                      </TableCell>
                      <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2 }}></TableCell>
                      <TableCell align="center" sx={{ border: "1px solid white", py: 1.2, px: 2, fontFamily: "karla", fontWeight: "400", fontSize: "16px" }}>
                        {summaryCurrencySymbol} {formatIndianPrice(totalAmount)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          );
        })}
      </Box>
    </Box>
  );
};

export default SubStockSetProductItem;
